import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Users, User, Calendar, Gamepad2, BarChart3, MapPin, UserCheck, Table, Radio, ChevronDown, LogOut, LogIn, Menu, X, Home, Building2, Shield, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get auth context - always call the hook unconditionally
  const { isAuthenticated, user, logout, isSuperAdmin, currentOrganization, organizations, switchOrganization } = useAuth();

  // Check if we're on admin routes
  const isAdminRoute = location.pathname.startsWith('/admin');

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  // Admin navigation items
  const adminNavItems = [
    { path: '/admin', label: 'Dashboard', icon: Home },
    { path: '/admin/organizations', label: 'Organizations', icon: Building2 },
    { path: '/admin/users', label: 'Users', icon: Users },
  ];

  // Regular user navigation items
  const mainNavItems = [
    { path: '/dashboard', label: 'Home', icon: Home },
    { path: '/live', label: 'Live', icon: Radio },
    { path: '/dashboard/tournaments', label: 'Tournaments', icon: Calendar },
    { path: '/dashboard/matches', label: 'Matches', icon: Gamepad2 },
    { path: '/dashboard/scorecards', label: 'Scorecards', icon: BarChart3 },
    { path: '/dashboard/points-table', label: 'Points', icon: Table },
  ];

  const moreNavItems = [
    { path: '/dashboard/teams', label: 'Teams', icon: Users },
    { path: '/dashboard/players', label: 'Players', icon: User },
    { path: '/dashboard/venues', label: 'Venues', icon: MapPin },
    { path: '/dashboard/umpires', label: 'Umpires', icon: UserCheck },
    { path: '/dashboard/import-export', label: 'Import/Export', icon: FileSpreadsheet },
  ];

  const allNavItems = [...mainNavItems, ...moreNavItems];

  const handleOrgSwitch = (org) => {
    switchOrganization(org.id);
    setShowOrgMenu(false);
  };

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4">
            <Link to={isAdminRoute ? '/admin' : '/dashboard'} className="flex items-center space-x-2">
              <img src="/logo.png" alt="Innings Pro" className="h-12 w-24" />
              <span className="text-lg md:text-xl font-bold text-gray-800">
                {isAdminRoute ? 'Admin Panel' : 'Innings Pro'}
              </span>
            </Link>

            {/* Organization Badge (non-admin only) */}
            {!isAdminRoute && currentOrganization && organizations.length > 1 && (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setShowOrgMenu(!showOrgMenu)}
                  onBlur={() => setTimeout(() => setShowOrgMenu(false), 200)}
                  className="flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <Building2 className="h-4 w-4" />
                  <span className="max-w-32 truncate">{currentOrganization.name}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>

                {showOrgMenu && (
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Switch Organization</p>
                    </div>
                    {organizations.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => handleOrgSwitch(org)}
                        className={`flex items-center space-x-2 px-4 py-2 text-sm w-full text-left ${
                          org.id === currentOrganization.id
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Building2 className="h-4 w-4" />
                        <span className="truncate">{org.name}</span>
                        {org.id === currentOrganization.id && (
                          <span className="ml-auto text-xs text-blue-500">Current</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Single org display */}
            {!isAdminRoute && currentOrganization && organizations.length === 1 && (
              <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm bg-gray-100 text-gray-600">
                <Building2 className="h-4 w-4" />
                <span className="max-w-32 truncate">{currentOrganization.name}</span>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-cricket-green hover:bg-gray-100"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {/* Admin Navigation */}
            {isAdminRoute ? (
              <>
                {adminNavItems.map(({ path, label, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      location.pathname === path
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-600 hover:text-purple-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{label}</span>
                  </Link>
                ))}
              </>
            ) : (
              <>
                {mainNavItems.map(({ path, label, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      location.pathname === path || (path === '/dashboard' && location.pathname === '/dashboard/')
                        ? 'bg-cricket-green text-white'
                        : 'text-gray-600 hover:text-cricket-green hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{label}</span>
                  </Link>
                ))}

                {/* More dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowMore(!showMore)}
                    onBlur={() => setTimeout(() => setShowMore(false), 200)}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                      ${moreNavItems.some(item => item.path === location.pathname)
                        ? 'bg-cricket-green text-white'
                        : 'text-gray-600 hover:text-cricket-green hover:bg-gray-100'
                      }`}
                  >
                    <span>More</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {showMore && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      {moreNavItems.map(({ path, label, icon: Icon }) => (
                        <Link
                          key={path}
                          to={path}
                          className={`flex items-center space-x-2 px-4 py-2 text-sm ${
                            location.pathname === path
                              ? 'bg-gray-100 text-cricket-green'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* User menu / Login */}
            <div className="ml-4 pl-4 border-l border-gray-200">
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    onBlur={() => setTimeout(() => setShowUserMenu(false), 200)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100"
                  >
                    <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">
                      {user?.username?.charAt(0).toUpperCase() || 'A'}
                    </div>
                    <span className="hidden md:inline">{user?.username}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.username}</p>
                        <p className="text-xs text-gray-500">{isSuperAdmin ? 'Super Admin' : 'Admin'}</p>
                      </div>
                      {isSuperAdmin && !isAdminRoute && (
                        <Link
                          to="/admin"
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 w-full"
                        >
                          <Shield className="h-4 w-4" />
                          <span>Admin Panel</span>
                        </Link>
                      )}
                      {isSuperAdmin && isAdminRoute && (
                        <Link
                          to="/dashboard"
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                        >
                          <Home className="h-4 w-4" />
                          <span>Dashboard</span>
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-cricket-green hover:bg-gray-100"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4">
            {/* Mobile Organization Selector */}
            {!isAdminRoute && currentOrganization && organizations.length > 1 && (
              <div className="px-3 pb-4 mb-4 border-b border-gray-200">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Organization</p>
                <div className="space-y-1">
                  {organizations.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => {
                        handleOrgSwitch(org);
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm w-full ${
                        org.id === currentOrganization.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Building2 className="h-4 w-4" />
                      <span className="truncate">{org.name}</span>
                      {org.id === currentOrganization.id && (
                        <span className="ml-auto text-xs text-blue-500">Current</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Single org display on mobile */}
            {!isAdminRoute && currentOrganization && organizations.length === 1 && (
              <div className="px-3 pb-4 mb-4 border-b border-gray-200">
                <div className="flex items-center space-x-2 px-3 py-2 rounded-md bg-gray-100 text-gray-600">
                  <Building2 className="h-4 w-4" />
                  <span className="text-sm">{currentOrganization.name}</span>
                </div>
              </div>
            )}

            <div className="space-y-1">
              {isAdminRoute ? (
                adminNavItems.map(({ path, label, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors ${
                      location.pathname === path
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-600 hover:text-purple-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{label}</span>
                  </Link>
                ))
              ) : (
                allNavItems.map(({ path, label, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors ${
                      location.pathname === path || (path === '/dashboard' && location.pathname === '/dashboard/')
                        ? 'bg-cricket-green text-white'
                        : 'text-gray-600 hover:text-cricket-green hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{label}</span>
                  </Link>
                ))
              )}
            </div>

            {/* Mobile User Section */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              {isAuthenticated ? (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3 px-3 py-2">
                    <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center text-lg font-bold">
                      {user?.username?.charAt(0).toUpperCase() || 'A'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.username}</p>
                      <p className="text-xs text-gray-500">{isSuperAdmin ? 'Super Admin' : 'Admin'}</p>
                    </div>
                  </div>
                  {isSuperAdmin && !isAdminRoute && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-purple-600 hover:bg-purple-50 w-full"
                    >
                      <Shield className="h-5 w-5" />
                      <span>Admin Panel</span>
                    </Link>
                  )}
                  {isSuperAdmin && isAdminRoute && (
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-gray-600 hover:bg-gray-50 w-full"
                    >
                      <Home className="h-5 w-5" />
                      <span>Dashboard</span>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-red-600 hover:bg-red-50 w-full"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-gray-600 hover:text-cricket-green hover:bg-gray-100"
                >
                  <LogIn className="h-5 w-5" />
                  <span>Login</span>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
